/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import memoize from 'memoize-one';

import {
  Action,
  InputValidationHandler,
  DATA_TYPE,
  OptionResponse,
  Output,
  ResolverType,
  ResponseCollectionBehavior,
  SelectionPolicy,
  Step,
  StepContext,
  StepQuestion,
  SystemVariable,
  TextResponse,
  TextResponseTextExpression,
  Variable
} from '../../../../../ts/action';
import { Language } from '../../../../../ts/skill';
import type { Entity, ModelsSlice } from '../../../../../ts/state';
import * as operations from '../../../../ia/store/operations';
import { OPTIONS_LIMIT } from '../../../../ia/util/validation/constants';
import {
  applyTransformationRuleOnCondition,
  buildOptionsList,
  countErrorsInCalloutResolver,
  countErrorsInClientActionsResolver,
  countErrorsInCondition,
  countErrorsInContext,
  countErrorsInInvokeActionResolver,
  countErrorsInText,
  ENTITY_TYPES,
  getDefaultErrorResponseByEntityType,
  getEntityType,
  getResponseText,
  getTitleFromResponseText,
  getYesNoTranslationForLang,
  invalidateConditionOnDelete,
  isPatternsEntity,
  isSystemEntity,
  NOT_FOUND_HANDLER,
  NOT_FOUND_MAX_TRIES_HANDLER,
  removeVariables,
  RESOLVER_PROPERTIES,
  RESPONSE_TYPES,
  splitTextIntoExpressionFormat,
  stepRefersToStep,
  updateEntityInCondition
} from '../../../util/actionUtil';
import { getVariableIdFromVariableValue } from '../../../util/variableUtil';
import {
  addResponseFromVariableResponse,
  setCollectVerbatimOnStep,
  setPrivacyOnStep,
  setSlotExamplesOnStep,
  updateAllowTopicSwitch,
  updateStepDynamicOptions
} from '../../actions/actions';
import * as actionTypes from './actionTypes';

const mapGlobalVariables = memoize((variables: Record<string, Variable>) => {
  return Object.values(variables).map(variable => variable.variable);
});

const mapSystemVariables = memoize(
  (variables: Record<string, SystemVariable>) => {
    return Object.values(variables).map(variable => variable.system_variable);
  }
);

const setActionTitle = (
  oldState: ModelsSlice,
  actionId: string,
  title: string
): ModelsSlice => {
  const state = { ...oldState };
  state.actions = { ...state.actions };
  state.actions[actionId] = { ...state.actions[actionId] };
  if (!state.actions[actionId].originalTitle) {
    //we need to store original title so that when we save the action we can check for references to the original title
    state.actions[actionId].originalTitle = state.actions[actionId].title;
  }
  state.actions[actionId].title = title;
  return state;
};

const setDisplayName = (
  oldState: ModelsSlice,
  actionId: string,
  displayName: string
): ModelsSlice => {
  const state = { ...oldState };
  state.actions = { ...state.actions };
  state.actions[actionId] = { ...state.actions[actionId] };

  state.actions[actionId].display_name = displayName;
  return state;
};

const updateActionOnSave = (
  oldState: ModelsSlice,
  actionId: string,
  condition: { intent: string },
  updated: string,
  entitiesToClear: string[]
): ModelsSlice => {
  const state = { ...oldState };
  const action: Action = { ..._.get(state, `actions.${actionId}`) };
  action.condition = condition;
  action.updated = updated;
  if (action.newAction) {
    delete action.newAction;
    if (!action.title) {
      action.title = action.tempTitle;
    }
    delete action.tempTitle;
  }
  if (action.originalTitle) {
    delete action.originalTitle;
  }
  state.actions = { ...state.actions };
  _.set(state, `actions.${actionId}`, action);
  if (entitiesToClear && entitiesToClear.length) {
    state.entities = { ...state.entities };
    entitiesToClear.forEach(entityId => {
      const entity = _.cloneDeep(_.get(state, `entities.${entityId}`));
      for (let i = 0; i < entity?.values?.length; i++) {
        delete entity.values[i].id;
        delete entity.values[i].originalValue;
        delete entity.values[i].previousValue;
      }
      _.set(state, `entities.${entityId}`, entity);
    });
  }
  // Copy the steps into the storeSteps after a save is made so they are in sync again.
  state.storeSteps = { ...state.steps };
  return state;
};

const updateActionTempTitle = (
  oldState: ModelsSlice,
  actionId: string
): ModelsSlice => {
  if (_.get(oldState, `actions[${actionId}]`, {}).newAction === true) {
    //if it is a new action, we need to create a temp title based on examples
    const state = { ...oldState };
    const examplesKeys = Object.keys(state.examples);
    if (examplesKeys.length > 0) {
      const action = { ..._.get(state, `actions[${actionId}]`, {}) };
      action.tempTitle = state.examples[examplesKeys[0]].text.substring(0, 64);
      while (action.tempTitle.length > 0) {
        if (
          /[\uD800-\uDBFF]/.test(
            action.tempTitle.substring(action.tempTitle.length - 1)
          )
        ) {
          // if high surrogate is found, we need to truncate it.
          action.tempTitle = action.tempTitle.slice(0, -1);
        } else {
          break;
        }
      }
      state.actions = { ...state.actions };
      _.set(state, `actions[${actionId}]`, action);
      return state;
    }
  }
  return oldState;
};

const addStepToAction = (
  oldState: ModelsSlice,
  actionId: string,
  stepId: string,
  newIndex: number,
  newStep: Step
): ModelsSlice => {
  const state = _.cloneDeep(oldState);
  const action = { ..._.get(state, `actions.${actionId}`) };
  const steps = action.steps || [];
  if (typeof newIndex !== 'number') {
    newIndex = steps.length;
  }
  action.steps = [...steps];
  action.steps.splice(newIndex, 0, stepId);
  const previousStepIndex = newIndex - 1;
  if (previousStepIndex > -1) {
    // change previous step's next_step to new step id
    const previousStepId = action.steps[previousStepIndex];
    const previousStep = state.steps[previousStepId];
    if (previousStep) {
      previousStep.next_step = newStep.step;
    }
  }

  state.actions = { ...state.actions };
  const result_variable =
    newStep.resolver?.invoke_action?.result_variable ||
    newStep.resolver?.callout?.result_variable;
  if (result_variable) {
    state.action_variables = {
      ...oldState.action_variables,
      [result_variable]: { variable: result_variable }
    };
  }
  _.set(state, `actions.${actionId}`, action);
  return state;
};

const setStepTitle = (
  oldState: ModelsSlice,
  stepId: string,
  title: string
): ModelsSlice => {
  const state = { ...oldState };
  const step = { ..._.get(state, `steps.${stepId}`) };
  if (title) {
    step.title = title;
    step.variableTitle = title;
  } else {
    delete step.title;
    step.variableTitle = getTitleFromResponseText(step) || 'No response';
  }

  state.steps = { ...state.steps };
  _.set(state, `steps.${stepId}`, step);
  return state;
};

const updateActionCondition = (
  oldState: ModelsSlice,
  actionId: string,
  condition: object
): ModelsSlice => {
  const state = { ...oldState };
  const action = { ..._.get(state, `actions.${actionId}`) };
  const oldCondition = action.condition;
  let newCondition = { and: {} };

  if (condition) {
    if (oldCondition?.and) {
      newCondition = { ...oldCondition };
      newCondition.and[1] = condition;
    } else {
      newCondition = {
        and: [oldCondition, condition]
      };
    }
  } else {
    if (oldCondition?.and) {
      newCondition = oldCondition.and[0];
    } else {
      newCondition = oldCondition;
    }
  }

  _.set(state, `actions.${actionId}.condition`, newCondition);
  return state;
};

const updateStepCondition = (
  oldState: ModelsSlice,
  stepId: string,
  condition: object // RAFA FIX THIS
): ModelsSlice => {
  const state = { ...oldState };
  const step = { ..._.get(state, `steps.${stepId}`) }; //shallow copy of step
  step.invalid_references = { ...step.invalid_references };
  const originalErrorCount = step.condition
    ? countErrorsInCondition(step.condition)
    : { entity: 0, value: 0, variable: 0, child_property: 0 };
  const currentErrorCount = condition
    ? countErrorsInCondition(condition)
    : { callout: 0, entity: 0, value: 0, variable: 0, child_property: 0 };
  let { entity, value, variable, child_property } = step.invalid_references; //get existing error counts
  entity = entity + (currentErrorCount.entity - originalErrorCount.entity);
  value = value + (currentErrorCount.value - originalErrorCount.value);
  variable =
    variable + (currentErrorCount.variable - originalErrorCount.variable);
  child_property =
    child_property +
    (currentErrorCount.child_property - originalErrorCount.child_property);
  step.invalid_references = {
    ...step.invalid_references,
    entity,
    value,
    variable,
    child_property
  };

  if (condition) {
    step.condition = condition;
  } else {
    delete step.condition;
  }

  state.steps = { ...state.steps };
  _.set(state, `steps.${stepId}`, step);
  return state;
};

const setStepText = (
  oldState: ModelsSlice,
  stepId: string,
  text: string,
  defaultValue: string
): ModelsSlice => {
  const state = { ...oldState };
  const { variables, variables_system } = state;
  const globalVars = mapGlobalVariables(variables);
  const systemVars = mapSystemVariables(variables_system);

  const step = _.cloneDeep(_.get(state, `steps.${stepId}`));
  const outputGeneric = _.get(step, 'output.generic');
  const textIndex = _.findIndex(outputGeneric, { response_type: 'text' });
  const text_expression = {
    concat: splitTextIntoExpressionFormat(
      text,
      globalVars,
      systemVars,
      defaultValue,
      variables
    )
  };

  if (!outputGeneric && text.length > 0) {
    _.set(step, 'output.generic[0]', {
      response_type: 'text',
      values: [{ text_expression }],
      selection_policy: 'sequential'
    });
  } else if (textIndex === -1 && text.length > 0) {
    const textResponse = {
      values: [{ text_expression }],
      response_type: 'text',
      selection_policy: 'sequential'
    };
    outputGeneric.unshift(textResponse);
  } else {
    const existingText = _.get(
      step,
      `output.generic[${textIndex}].values[0].text_expression`
    );
    const existingInvalidVars = existingText
      ? countErrorsInText(existingText.concat)
      : 0;
    const latestInvalidVars = countErrorsInText(text_expression.concat);
    step.invalid_references.variable =
      step.invalid_references.variable +
      (latestInvalidVars - existingInvalidVars);
    if (text.length > 0) {
      _.set(step, `output.generic[${textIndex}].values[0]`, {
        text_expression
      });
    } else {
      _.pullAt(_.get(step, `output.generic[${textIndex}].values`), [0]);
    }
  }
  step.responseText = text || 'No response'; //this is used in the navigation tile
  if (!step.title) {
    //if the title is not set we need to update the variable's description
    step.variableTitle = removeVariables(step.responseText);
    let stepVariable = _.get(state, `action_variables.${step.variable}`);
    if (stepVariable) {
      stepVariable = { ...stepVariable };
      stepVariable.title = step.variableTitle;
      state.action_variables = { ...state.action_variables };
      _.set(state, `action_variables.${step.variable}`, stepVariable);
    }
  }
  state.steps = { ...state.steps };
  _.set(state, `steps.${stepId}`, step);
  return state;
};

const getStepResolverDefaultContent = (type: string): any => {
  switch (type) {
    case ResolverType.SEARCH:
      return {
        type: type,
        response: {
          query: '',
          query_type: 'natural_language',
          response_type: 'search_skill'
        }
      };
    default:
      return {
        type
      };
  }
};

const deleteReferencesToStepVariable = (
  state: ModelsSlice,
  stepId: string = null,
  entityId: string = null,
  variableId: string = null
): ModelsSlice => {
  const { steps } = state;
  const stepsToUpdate = {};
  for (const key in steps) {
    //we are deleting a step. This step's variable may be referenced in one of the other steps
    //if we are referencing it we must mark that step as 'invalid'
    if (key === stepId) {
      continue;
    }
    const originalStep = steps[key];
    if (stepRefersToStep(originalStep, stepId, variableId)) {
      const step = _.cloneDeep(originalStep);
      if (stepId && _.get(step, 'resolver.step') === stepId) {
        step.invalid_references.jump_to_step++;
        step.resolver.step = null;
      }
      if (step.output && step.output.generic) {
        const textOutput = _.find(step.output.generic, {
          response_type: 'text'
        }) as TextResponse;
        let updateResponseText = false;
        if (textOutput && textOutput.values) {
          const value = _.find(
            textOutput.values,
            'text_expression'
          ) as TextResponseTextExpression;
          if (value) {
            for (let i = 0; i < value.text_expression.concat.length; i++) {
              const item = value.text_expression.concat[i];
              if ('variable' in item && item.variable === variableId) {
                item.variable = null;
                step.invalid_references.variable++;
                updateResponseText = true;
              }
            }
          }
        }
        if (updateResponseText) {
          step.responseText = getResponseText(step);
        }
      }
      const { condition } = step;
      if (condition) {
        invalidateConditionOnDelete(
          condition,
          variableId,
          entityId,
          step.invalid_references
        );
      }
      const { context } = step;
      if (context) {
        invalidateConditionOnDelete(
          context,
          variableId,
          null,
          step.invalid_references
        );
      }
      const { resolver } = step;
      if (resolver) {
        if (
          resolver[RESOLVER_PROPERTIES.INVOKE_ACTION] &&
          resolver[RESOLVER_PROPERTIES.INVOKE_ACTION].parameters
        ) {
          const { parameters } = resolver[RESOLVER_PROPERTIES.INVOKE_ACTION];
          for (const parameter of parameters) {
            if (parameter.value && parameter.value.variable === variableId) {
              parameter.value.variable = null;
              step.invalid_references.variable++;
            }
          }
        } else if (resolver.type === ResolverType.REPLAY && resolver.clear) {
          const { clear } = resolver;
          resolver.clear = clear.filter(
            variable => variable.variable !== variableId
          );
        } else if (
          resolver.type === ResolverType.CALL_AN_EXTENSION &&
          resolver.callout
        ) {
          const requestMap = _.get(resolver, 'callout.request_mapping', {});
          let parameters = [];
          if (!_.isEmpty(requestMap)) {
            const reqProps = Object.keys(requestMap);
            reqProps.forEach(prop => {
              parameters = parameters.concat(requestMap[prop]);
            });
          }
          parameters.forEach(param => {
            const paramVariableId = getVariableIdFromVariableValue(param.value);
            if (paramVariableId === variableId) {
              step.invalid_references.variable++;
              param.value = { variable: null };
            }
          });
        } else if (resolver.type === ResolverType.CLIENT_ACTIONS) {
          const clientActions = resolver.client_actions;
          if (clientActions) {
            clientActions.forEach(clientAction => {
              if (clientAction.parameters) {
                clientAction.parameters.forEach(parameter => {
                  if (
                    parameter.value &&
                    'variable' in parameter.value &&
                    parameter.value.variable === variableId
                  ) {
                    parameter.value.variable = null;
                    step.invalid_references.variable++;
                  }
                });
              }
            });
          }
        }
      }
      stepsToUpdate[key] = step;
    }
  }
  const newSteps = { ...state.steps, ...stepsToUpdate };
  return { ...state, steps: newSteps };
};

const updateEndStepBehavior = (
  oldState: ModelsSlice,
  actionId: string,
  stepId: string,
  type: string,
  // RAFA-- Figure out resolver
  updatedResolver = {}
): ModelsSlice => {
  let state = { ...oldState };
  state.steps = { ...state.steps };
  const step = { ...state.steps[stepId] };

  const currentResolver = _.get(step, 'resolver.type');

  if (
    (currentResolver === ResolverType.INVOKE_ANOTHER_ACTION_AND_END ||
      currentResolver === ResolverType.INVOKE_ANOTHER_ACTION) &&
    type !== ResolverType.INVOKE_ANOTHER_ACTION_AND_END &&
    type !== ResolverType.INVOKE_ANOTHER_ACTION
  ) {
    //if we are removing the call subaction resolver we must also invalidate all references to the result_variable
    const resultVarId = _.get(step, 'resolver.invoke_action.result_variable');
    if (resultVarId) {
      state = deleteReferencesToStepVariable(state, null, null, resultVarId);
    }
  } else if (
    currentResolver === ResolverType.CALL_AN_EXTENSION &&
    type !== ResolverType.CALL_AN_EXTENSION
  ) {
    //if we are removing the call extension resolver we must also invalidate all references to the result_variable
    const resultVarId = _.get(step, 'resolver.callout.result_variable');
    if (resultVarId) {
      state = deleteReferencesToStepVariable(state, null, null, resultVarId);
    }
  } else if (
    currentResolver === ResolverType.CLIENT_ACTIONS &&
    type !== ResolverType.CLIENT_ACTIONS
  ) {
    const resultVarId = _.get(
      step,
      'resolver.client_actions[0].result_variable'
    );
    if (resultVarId) {
      state = deleteReferencesToStepVariable(state, null, null, resultVarId);
    }
  }

  const resolver = getStepResolverDefaultContent(type);

  _.merge(resolver, updatedResolver);

  if (
    type === ResolverType.INVOKE_ANOTHER_ACTION ||
    type === ResolverType.INVOKE_ANOTHER_ACTION_AND_END ||
    type === ResolverType.CALL_AN_EXTENSION ||
    type === ResolverType.CLIENT_ACTIONS ||
    type === ResolverType.SEARCH
  ) {
    let path = 'callout';
    switch (type) {
      case ResolverType.INVOKE_ANOTHER_ACTION:
      case ResolverType.INVOKE_ANOTHER_ACTION_AND_END:
        path = 'invoke_action';
        break;
      case ResolverType.CALL_AN_EXTENSION:
        path = 'callout';
        break;
      case ResolverType.CLIENT_ACTIONS:
        path = 'client_actions[0]';
        break;
      case ResolverType.SEARCH:
        path = 'response.after_generation';
        break;
      default:
        path = 'callout';
        break;
    }
    if (!_.get(resolver, `${path}.result_variable`)) {
      //result variable is not set!
      const actionVariables = { ...state.action_variables } || {};
      let idx = 1;
      let varId = `${stepId}_result_${idx}`;
      while (Object.keys(actionVariables).indexOf(varId) !== -1) {
        varId = `${stepId}_result_${++idx}`;
      }
      const resultVariable = {
        variable: varId,
        privacy: {
          enabled: resolver?.privacyEnabled ?? false
        }
      };
      actionVariables[varId] = resultVariable;
      _.set(resolver, `${path}.result_variable`, varId);
      state.action_variables = actionVariables;
    } else if (_.get(resolver, `${path}.result_variable`)) {
      //result variable already exists but need to update the privacy
      let resultVariable = null;
      if (currentResolver === ResolverType.INVOKE_ANOTHER_ACTION) {
        resultVariable = resolver?.invoke_action?.result_variable;
      } else if (currentResolver === ResolverType.CALL_AN_EXTENSION) {
        resultVariable = resolver?.callout?.result_variable;
      }
      state.action_variables[resultVariable] = {
        ...state.action_variables[resultVariable],
        privacy: {
          enabled: resolver?.privacyEnabled
        }
      };
    }
  }
  const invalidReferences = step.invalid_references
    ? { ...step.invalid_references }
    : { callout: 0, variable: 0, sub_action: 0 };

  /*
   * count the errors in the existing resolver (an error is counted as a 'variable' or an 'action' or 'catalog_item_id' whose
   * value is `null`. The current error state is determined by counting the number of errors that exist after the update
   * minus the number of errors from before the update. The errors are counted in step.resolver (prior state) and the latest
   * resolver (resolver).
   */
  if (
    type === ResolverType.INVOKE_ANOTHER_ACTION ||
    type === ResolverType.INVOKE_ANOTHER_ACTION_AND_END
  ) {
    const existingVarErrors = countErrorsInInvokeActionResolver(step.resolver);
    const latestVarErrors = countErrorsInInvokeActionResolver(resolver);
    invalidReferences.variable =
      invalidReferences.variable +
      (latestVarErrors.variable - existingVarErrors.variable);
    invalidReferences.sub_action =
      invalidReferences.sub_action +
      (latestVarErrors.sub_action - existingVarErrors.sub_action);
  } else if (type === ResolverType.CALL_AN_EXTENSION) {
    const existingCalloutErrors = countErrorsInCalloutResolver(step.resolver);
    const latestCalloutErrors = countErrorsInCalloutResolver(resolver);
    invalidReferences.variable =
      invalidReferences.variable +
      (latestCalloutErrors.variable - existingCalloutErrors.variable);

    invalidReferences.callout =
      invalidReferences.callout +
      (latestCalloutErrors.callout - existingCalloutErrors.callout);
  } else if (type === ResolverType.CLIENT_ACTIONS) {
    const existingClientActionsErrors = countErrorsInClientActionsResolver(
      step.resolver
    );
    const latestClientActionsErrors =
      countErrorsInClientActionsResolver(resolver);
    invalidReferences.variable =
      invalidReferences.variable +
      (latestClientActionsErrors - existingClientActionsErrors);
  }

  step.invalid_references = invalidReferences;
  step.resolver = resolver;
  state.steps[stepId] = step;
  return state;
};

const reorderSteps = (
  oldState: ModelsSlice,
  actionId: string,
  sourceIndex: number,
  destinationIndex: number
): ModelsSlice => {
  const state = { ...oldState };
  state.steps = { ...state.steps };
  state.actions = { ...state.actions };
  const { steps } = state;
  const currentAction = {
    ..._.get(state, `actions.${actionId}`),
    steps: [..._.get(state, `actions.${actionId}.steps`)]
  };

  const [movingStep] = currentAction.steps.splice(sourceIndex, 1); //remove the step we are moving from the array
  currentAction.steps.splice(destinationIndex, 0, movingStep); //insert the step at the new location
  for (let i = 0; i < currentAction.steps.length; i++) {
    //iterate through all steps and update the next_step prop
    const key = currentAction.steps[i];
    const step: Step = { ...steps[key] };
    if (i < currentAction.steps.length - 1) {
      step.next_step = currentAction.steps[i + 1];
    } else {
      delete step.next_step;
    }
    //some of these steps may be 'new steps'.. we do not want them to scroll into view as a result of this update
    delete step.newStep;
    _.set(state, `steps.${key}`, step);
  }
  _.set(state, `actions.${actionId}`, currentAction);
  return state;
};

const updateEntityReferencesToStepVariable = (
  state: ModelsSlice,
  stepId: string = null,
  entityId: string = null,
  newEntityId: string = null,
  variableId: string = null
): ModelsSlice => {
  const { steps } = state;
  const stepsToUpdate = {};
  for (const key in steps) {
    if (key === stepId) {
      continue;
    }
    const originalStep = steps[key];
    if (stepRefersToStep(originalStep, stepId, variableId)) {
      const step = _.cloneDeep(originalStep);
      const { condition } = step;
      if (condition) {
        step.condition = updateEntityInCondition(
          condition,
          entityId,
          newEntityId
        );
      }
      stepsToUpdate[key] = step;
    }
  }
  const newSteps = { ...state.steps, ...stepsToUpdate };
  return { ...state, steps: newSteps };
};

const deleteStep = (
  state: ModelsSlice,
  actionId: string,
  entityId: string,
  stepId: string,
  variableId: string,
  resultVariableId: string
): ModelsSlice => {
  //we've received a request to delete the current step. As a part of deleting a step we must
  //check all other steps in the action to see if any of them reference this step's variable/entity

  let newState = deleteReferencesToStepVariable(
    state,
    stepId,
    entityId,
    variableId
  );
  if (resultVariableId) {
    newState = deleteReferencesToStepVariable(
      newState,
      null,
      null,
      resultVariableId
    );
  }

  const currentAction = { ..._.get(newState, `actions.${actionId}`) };
  currentAction.steps = [...currentAction.steps];
  currentAction.variables = currentAction.variable?.filter(
    v => v !== variableId && v !== resultVariableId
  );
  const { steps } = newState;
  const stepsToUpdate = {};
  const stepIndex = currentAction.steps.indexOf(stepId);
  let stepIdToUpdate = null;

  if (
    stepIndex === currentAction.steps.length - 1 &&
    currentAction.steps.length > 1
  ) {
    //the step deleted was the last step, in this case we remove last step id from array of steps
    //then get the second from last step and update its next_step to be null (delete)
    stepIdToUpdate = currentAction.steps[currentAction.steps.length - 2];
    const stepToUpdate = stepsToUpdate[stepIdToUpdate] || {
      ...steps[stepIdToUpdate]
    };
    delete stepToUpdate.next_step;
    stepsToUpdate[stepIdToUpdate] = stepToUpdate;
    currentAction.steps.splice(stepIndex, 1);
  } else if (stepIndex > 0) {
    //the deleted step was some where in the 'middle' of the list of steps, need to update the step that was prior
    //to the deleted step to point to the step after the current step as the 'next step'
    const prevStep = currentAction.steps[stepIndex - 1];
    const updatedStep = stepsToUpdate[prevStep] || { ...steps[prevStep] };
    updatedStep.next_step = currentAction.steps[stepIndex + 1];
    stepsToUpdate[prevStep] = updatedStep;
  }
  currentAction.steps.splice(stepIndex, 1);
  const newSteps = { ...newState.steps, ...stepsToUpdate };
  const finalState = {
    ...newState,
    actions: { ...newState.actions },
    steps: newSteps
  };
  finalState.actions[actionId] = currentAction;
  return finalState;
};

const updateStepValidation = (
  oldState: ModelsSlice,
  stepId: string,
  numberOfTurns: number,
  respondWith: string[] | string,
  useLaunchModeDefault?: boolean,
  inputValidationHandler?: InputValidationHandler,
  repeatOnReprompt?: boolean
): ModelsSlice => {
  const state = { ...oldState };
  state.steps = { ...state.steps };
  const step = _.cloneDeep(_.get(state, `steps.${stepId}`)) as Step;
  const { variables, variables_system } = state;
  const globalVars = mapGlobalVariables(variables);
  const systemVars = mapSystemVariables(variables_system);

  const handlers = step.handlers || [];

  const notFoundHandlerIndex = _.findIndex(handlers, {
    type: 'not_found',
    handler: NOT_FOUND_HANDLER
  });

  let responseValues;
  if (_.isArray(respondWith)) {
    responseValues = respondWith.map(response => ({
      text_expression: {
        concat: splitTextIntoExpressionFormat(
          response,
          globalVars,
          systemVars,
          null,
          variables
        )
      }
    }));
  } else {
    responseValues = [
      {
        text_expression: {
          concat: splitTextIntoExpressionFormat(
            respondWith,
            globalVars,
            systemVars,
            null,
            variables
          )
        }
      }
    ];
  }

  let notFoundHandler;
  const notFoundHandlerOutput = {
    generic: [
      {
        response_type: 'text',
        selection_policy: SelectionPolicy.INCREMENTAL,
        values: responseValues
      }
    ]
  };
  if (notFoundHandlerIndex > -1) {
    notFoundHandler = {
      ...handlers[notFoundHandlerIndex],
      output: notFoundHandlerOutput
    };
    handlers[notFoundHandlerIndex] = notFoundHandler;
  } else {
    notFoundHandler = {
      type: 'not_found',
      title: NOT_FOUND_HANDLER,
      output: notFoundHandlerOutput,
      handler: NOT_FOUND_HANDLER,
      resolver: {
        type: 'prompt_again'
      }
    };
    handlers.push(notFoundHandler);
  }

  const inputHandlerOutput = {
    generic: [
      {
        response_type: 'text' as 'text', // Typescript is weird sometimes
        selection_policy: SelectionPolicy.INCREMENTAL,
        values: responseValues
      }
    ]
  };

  const inputValidationHandlerIndex = _.findIndex(handlers, {
    type: 'input_validation'
  });
  if (inputValidationHandler) {
    if (inputValidationHandlerIndex > -1) {
      if (inputValidationHandler?.input_constraints?.length === 0) {
        handlers.splice(inputValidationHandlerIndex, 1);
        if (handlers.length - 2 >= 0) {
          handlers[handlers.length - 2].next_handler = NOT_FOUND_HANDLER;
        }
      } else {
        const newInputValidationHandler: InputValidationHandler = {
          ...handlers[inputValidationHandlerIndex],
          ...inputValidationHandler,
          output: inputHandlerOutput
        };
        handlers[inputValidationHandlerIndex] = newInputValidationHandler;
      }
    } else if (inputValidationHandler?.input_constraints?.length > 0) {
      handlers.push({ ...inputValidationHandler, output: inputHandlerOutput });
    }
  }

  const notFoundMaxTriesHandlerIndex = _.findIndex(handlers, {
    type: 'not_found_max_tries',
    handler: NOT_FOUND_MAX_TRIES_HANDLER
  });

  let notFoundMaxTriesHandler;

  if (notFoundMaxTriesHandlerIndex > -1) {
    notFoundMaxTriesHandler = {
      ...handlers[notFoundMaxTriesHandlerIndex],
      resolver: {
        type: 'fallback'
      }
    };
    handlers[notFoundMaxTriesHandlerIndex] = notFoundMaxTriesHandler;
  } else {
    notFoundMaxTriesHandler = {
      type: 'not_found_max_tries',
      title: NOT_FOUND_MAX_TRIES_HANDLER,
      handler: NOT_FOUND_MAX_TRIES_HANDLER,
      resolver: {
        type: 'fallback'
      }
    };
    handlers.push(notFoundMaxTriesHandler);
  }
  // sort the handlers in order of ['input_validation', 'not_found', 'not_found_max_tries'] order
  handlers.sort((a, b) => {
    if (a.type === 'input_validation') {
      return -1;
    }
    if (b.type === 'input_validation') {
      return 1;
    }
    if (a.type === 'not_found') {
      return -1;
    }
    if (b.type === 'not_found') {
      return 1;
    }
    return 0;
  });
  // fix next_handler cursor of all handlers
  for (let i = 0; i < handlers.length; i++) {
    if (i === 0) {
      handlers[i].next_handler = undefined;
    } else {
      handlers[i - 1].next_handler = handlers[i].handler;
    }
    if (i === handlers.length - 1) {
      handlers[i].next_handler = undefined;
    }
  }

  const question = step.question || {};
  if (useLaunchModeDefault) {
    delete question.max_tries;
  } else {
    question.max_tries = numberOfTurns;
  }

  // Set repeat_on_reprompt if response type is not option, date, or response_from_data_type
  for (const i in step?.output?.generic) {
    const response = step.output.generic[i];

    switch (response?.response_type) {
      case RESPONSE_TYPES.option:
      case RESPONSE_TYPES.date:
      case RESPONSE_TYPES.response_from_data_type:
        break;
      default:
        response.repeat_on_reprompt = !!repeatOnReprompt;
    }
  }

  step.question = question;
  step.handlers = handlers;
  _.set(state, `steps.${stepId}`, step);
  return state;
};

const defineCustomerResponse = (
  oldState: ModelsSlice,
  stepId: string,
  dataTypeId: string,
  entityId: string,
  language: Language,
  // RAFA FIGURE THIS OUT
  launchModeSettings: { enabled: true }
): ModelsSlice => {
  const entityType = getEntityType(entityId, oldState.entities);
  const defaultNumOfTurns = launchModeSettings?.enabled ? undefined : 3;

  const state = updateStepValidation(
    oldState,
    stepId,
    defaultNumOfTurns,
    getDefaultErrorResponseByEntityType(entityType, language)
  );

  const steps = { ...state.steps };
  const step = { ...steps[stepId] };
  const actionVariables = { ...state.action_variables };
  const stepVariable = { ...actionVariables[stepId] };

  step.question = step.question || {};
  if (dataTypeId) {
    delete step.question.entity;
    step.question.data_type = dataTypeId;
    stepVariable.data_type = dataTypeId;
  } else {
    delete step.question.data_type;
    step.question.entity = entityId;
  }
  switch (entityType) {
    case ENTITY_TYPES.confirmation.id: {
      _.set(
        step,
        'question.response_collection_behavior',
        ResponseCollectionBehavior.ALWAYS_ASK
      );
      break;
    }
    default:
      break;
  }

  if (entityType === ENTITY_TYPES.free_text.id) {
    delete step.question.data_type;
    delete step.question.entity;
  } else {
    delete step.question.free_text;
  }

  steps[stepId] = step;
  state.steps = steps;
  actionVariables[stepId] = stepVariable;
  state.action_variables = actionVariables;

  return state;
};

const deleteStepOptions = (
  oldState: ModelsSlice,
  stepId: string,
  dataTypeId: string,
  entity: Entity,
  entityId: string
): ModelsSlice => {
  let state = { ...oldState };
  const currentStep: Step = _.cloneDeep(_.get(state, `steps.${stepId}`));
  const outputGeneric: Output['generic'] = _.get(currentStep, 'output.generic');

  let targetOptionsIndex = -1;

  if (dataTypeId) {
    targetOptionsIndex = _.findIndex(
      outputGeneric,
      o => o.response_type === RESPONSE_TYPES.response_from_data_type
    );
  } else if (entity) {
    targetOptionsIndex = _.findIndex(outputGeneric, o => {
      if (o.response_type === RESPONSE_TYPES.date) {
        return true;
      }
      if (o.response_type !== RESPONSE_TYPES.option) {
        return false;
      }
      const options = (o as OptionResponse).options;
      if (!Array.isArray(options) || options.length === 0) {
        return false;
      }
      for (const option of options) {
        if (option.label !== _.get(option, 'value.input.text')) {
          return false;
        }
        if (_.find(entity.values, item => item.value === option.label)) {
          return true;
        }
        return false;
      }
    });
  } else {
    targetOptionsIndex = _.findIndex(
      outputGeneric,
      output =>
        output.response_type === RESPONSE_TYPES.option ||
        output.response_type === RESPONSE_TYPES.response_from_data_type ||
        output.response_type === RESPONSE_TYPES.response_from_variable ||
        output.response_type === RESPONSE_TYPES.date
    );
  }

  if (targetOptionsIndex > -1) {
    //remove response
    outputGeneric.splice(targetOptionsIndex, 1);
    _.set(currentStep, 'output.generic', outputGeneric);
  }
  if (entityId || dataTypeId) {
    state = deleteReferencesToStepVariable(
      state,
      stepId,
      entityId,
      currentStep.variable
    );
  }
  if (currentStep.question) {
    delete currentStep.question;
  }
  _.set(state, `steps.${stepId}`, currentStep);
  return state;
};

const removeCustomerResponse = (
  oldState: ModelsSlice,
  stepId: string,
  dataTypeId: string,
  entityId: string,
  entity: Entity
): ModelsSlice => {
  const state = deleteStepOptions(
    oldState,
    stepId,
    dataTypeId,
    entity,
    entityId
  );

  const steps = { ...state.steps };
  const step = _.cloneDeep(steps[stepId]);
  if (step?.invalid_references?.data_type > 0) {
    step.invalid_references.data_type -= 1;
  }

  if (step.question) {
    delete step.question;
  }

  if (step.handlers && step.handlers.length > 0) {
    const handlers = [...step.handlers];

    const inputConstraintsIndex = _.findIndex(handlers, {
      type: 'input_validation'
    });
    if (inputConstraintsIndex > -1) {
      const next = handlers[inputConstraintsIndex].next_handler;
      if (next) {
        const prevIndex = _.findIndex(handlers, {
          next_handler: handlers[inputConstraintsIndex].handler
        });
        if (prevIndex > -1) {
          handlers[prevIndex].next_handler = next;
        }
      }
      handlers.splice(inputConstraintsIndex, 1);
    }

    const notFoundIndex = _.findIndex(handlers, {
      type: 'not_found',
      handler: NOT_FOUND_HANDLER
    });

    if (notFoundIndex > -1) {
      //get the handler this one is pointing to (as next handler)
      const next = handlers[notFoundIndex].next_handler;
      if (next) {
        const index = _.findIndex(handlers, {
          next_handler: handlers[notFoundIndex].handler
        });
        if (index !== -1) {
          handlers[index].next_handler = next;
        }
      }
      handlers.splice(notFoundIndex, 1);
    }

    const maxTriesIndex = _.findIndex(handlers, {
      type: 'not_found_max_tries',
      handler: NOT_FOUND_MAX_TRIES_HANDLER
    });

    if (maxTriesIndex > -1) {
      //get the handler this one is pointing to (as next handler)
      const next = handlers[maxTriesIndex].next_handler;
      if (next) {
        const index = _.findIndex(handlers, {
          next_handler: handlers[notFoundIndex].handler
        });
        if (index !== -1) {
          handlers[index].next_handler = next;
        }
      }
      handlers.splice(maxTriesIndex, 1);
    }
    step.handlers = handlers;
  }

  steps[stepId] = step;
  state.steps = steps;

  return state;
};

const updateDatepicker = (
  oldState: ModelsSlice,
  stepId: string,
  entityId: string
): ModelsSlice => {
  const state = { ...oldState };
  const steps = { ...state.steps };
  const step = _.cloneDeep(steps[stepId]);
  const currentStepOutput = step.output || ({} as Output);

  const datePicker = _.find(
    currentStepOutput.generic,
    { response_type: RESPONSE_TYPES.date },
    0
  );

  // if a date response type is not present
  if (entityId && !datePicker) {
    if (!currentStepOutput.generic) {
      currentStepOutput.generic = [];
    }

    currentStepOutput.generic.push({
      response_type: 'date',
      repeat_on_reprompt: true
    });

    _.set(step, 'output', currentStepOutput);
    steps[stepId] = step;
  }

  state.steps = steps;
  return state;
};

const updateStepMaxHits = (
  oldState: ModelsSlice,
  stepId: string,
  newMaxHits: number
): ModelsSlice => {
  const state = { ...oldState };
  const steps = { ...state.steps };
  const step = { ...steps[stepId] };

  _.set(step, 'max_hits', newMaxHits);
  steps[stepId] = step;
  state.steps = steps;

  return state;
};

const updateStepYesNoOptions = (
  oldState: ModelsSlice,
  language: Language = Language.English,
  stepId: string,
  entityId: string
): ModelsSlice => {
  const state = { ...oldState };
  const steps = { ...state.steps };
  const step = _.cloneDeep(steps[stepId]);
  const currentStepOutput = step.output || ({} as Output);
  let options = _.find(
    currentStepOutput.generic,
    { response_type: ENTITY_TYPES.option.id },
    0
  );
  const values = getYesNoTranslationForLang(language);
  if (entityId && !options) {
    //add options to the step
    if (!currentStepOutput.generic) {
      currentStepOutput.generic = [];
    }
    options = buildOptionsList(values);
    currentStepOutput.generic.push({
      options,
      response_type: 'option',
      repeat_on_reprompt: true
    } as any);
    _.set(step, 'output', currentStepOutput);
    steps[stepId] = step;
  }
  state.steps = steps;
  return state;
};

const setDataTypeIdToStepQuestion = (step: Step, dataTypeId: string): Step => {
  const modifiedStep = _.cloneDeep(step);

  delete modifiedStep.question?.entity;
  delete modifiedStep.question?.free_text;
  _.set(modifiedStep, 'question.data_type', dataTypeId);

  return modifiedStep;
};

const isOutputValidOptionResponse = (
  output: OptionResponse,
  entity: Entity
): boolean => {
  if (output.response_type !== RESPONSE_TYPES.option) {
    return false;
  }

  const currentOptions = output.options;

  if (
    !Array.isArray(currentOptions) ||
    currentOptions.length === 0 ||
    currentOptions.length !== _.get(entity, 'values.length')
  ) {
    return false;
  }

  return _.every(currentOptions, option => {
    // if the label does not match the value.input.text it doesn't match
    if (option.label !== _.get(option, 'value.input.text')) {
      return false;
    }

    // if the options label is within the entity it matches
    if (_.find(entity.values, item => item.value === option.label)) {
      return true;
    }

    // default is it doesn't match
    return false;
  });
};

const updateEntityValueReferencesInStepConditions = (
  stepsMap: Record<string, Step>,
  entityName: string,
  // RAFA FIGURE OUT
  optionsDiffMap
): Record<string, Step> => {
  const updatedSteps = {};
  const stepIds = Object.keys(stepsMap);
  const changesToApply = {};
  Object.keys(optionsDiffMap).forEach(key => {
    if (optionsDiffMap[key] && optionsDiffMap[key].delete) {
      changesToApply[key] = null;
    } else {
      if (
        optionsDiffMap[key] &&
        optionsDiffMap[key].previousValue &&
        optionsDiffMap[key].previousValue !== optionsDiffMap[key].value
      ) {
        changesToApply[optionsDiffMap[key].previousValue] =
          optionsDiffMap[key].value;
      }
    }
  });

  stepIds.forEach(stepId => {
    const step = stepsMap[stepId];
    if (step.condition) {
      let invalidValuesReferences = step.invalid_references.value;
      const condition = applyTransformationRuleOnCondition(
        step.condition,
        (key, subtree) => {
          if (
            _.isArray(subtree) &&
            subtree.length === 2 &&
            _.isPlainObject(subtree[1])
          ) {
            if (
              subtree[1]?.from_entity === entityName &&
              changesToApply.hasOwnProperty(subtree[1]?.value)
            ) {
              if (changesToApply[subtree[1].value] === null) {
                invalidValuesReferences++;
              }
              return [
                key,
                [
                  subtree[0],
                  {
                    ...subtree[1],
                    value: changesToApply[subtree[1].value]
                  }
                ]
              ];
            } else if (Array.isArray(subtree[1]?.collection)) {
              const collection = subtree[1].collection.map(item => {
                if (
                  item?.from_entity === entityName &&
                  changesToApply.hasOwnProperty(item?.value)
                ) {
                  if (changesToApply[item.value] === null) {
                    invalidValuesReferences++;
                  }
                  return {
                    ...item,
                    value: changesToApply[item.value]
                  };
                }
                return { ...item };
              });
              return [key, [subtree[0], { ...subtree[1], collection }]];
            }
          }
          return [key, subtree];
        }
      );
      if (condition !== step.condition) {
        const newStep = {
          ...step,
          invalid_references: {
            ...step.invalid_references,
            value: invalidValuesReferences
          },
          condition
        };
        updatedSteps[stepId] = newStep;
      }
    }
  });

  return updatedSteps;
};

const findOptionsResponseIndexMatchingEntity = (
  outputGeneric: Output['generic'],
  entity: Entity
): number =>
  _.findIndex(outputGeneric, output =>
    isOutputValidOptionResponse(output as OptionResponse, entity)
  );

const updateStepOptions = (
  oldState: ModelsSlice,
  actionId: string,
  stepId: string,
  dataTypeOperation: string,
  dataTypeId: string,
  entityId: string,
  options: {
    value: string;
    id: number;
    synonyms: [];
    originalValue?: string;
    previousValue?: string;
  }[],
  optionsDiffMap: {
    [key: number | string]: {
      value: string;
      id: number;
      delete: boolean;
      originalValue?: string;
      previousValue?: string;
    };
  },
  entity: Entity
): ModelsSlice => {
  const state = { ...oldState };

  let currentStep: Step = _.cloneDeep(_.get(state, `steps.${stepId}`));
  const outputGeneric: Output['generic'] = _.get(
    currentStep,
    'output.generic',
    []
  );

  const outputContainsDataTypeResponse = outputGeneric.some(
    output => output.response_type === RESPONSE_TYPES.response_from_data_type
  );

  // we want to update the output if a dataType
  // has been linked or unlinked within this update
  const wasDataTypeChanged =
    dataTypeOperation === operations.CREATE ||
    (!dataTypeId && outputContainsDataTypeResponse);

  // if we have a dataTypeId, it is a saved response and we
  // want to make sure the question in the step has the relevant info
  if (dataTypeId) {
    currentStep = setDataTypeIdToStepQuestion(currentStep, dataTypeId);
  } else {
    // when we don't have data type id but have an associated data type
    // it means we should unlink the saved response
    // we need to delete the data type response
    _.remove(
      outputGeneric,
      output => output.response_type === RESPONSE_TYPES.response_from_data_type
    );
  }

  const isOptionsEntity =
    !isSystemEntity(entityId) && !isPatternsEntity(entity);

  // if we are updating an entity that is created by Tanya
  if (isOptionsEntity) {
    let optionsResponseIndexMatchingEntity = -1;

    if (entity) {
      optionsResponseIndexMatchingEntity =
        findOptionsResponseIndexMatchingEntity(outputGeneric, entity);
    } else {
      optionsResponseIndexMatchingEntity = _.findIndex(outputGeneric, {
        response_type: RESPONSE_TYPES.option as any
      });
    }

    // create a slice of options
    const rpOptions = options.slice(0, OPTIONS_LIMIT).map(option => {
      return {
        label: option.value,
        value: {
          input: {
            text: option.value
          }
        }
      };
    });

    // we match up the options diff key with the option numerical id
    // as when an option is first created it is represented within the diff map by id
    // and when it is updated subsequently it is represented in the diff map by its original value
    const isOptionNewlyCreated: Boolean = options.every(option => {
      const matchingOptionDiff = optionsDiffMap?.[option.id];
      return Boolean(matchingOptionDiff) && matchingOptionDiff?.delete !== true;
    });

    if (
      isOptionNewlyCreated ||
      wasDataTypeChanged ||
      optionsResponseIndexMatchingEntity > -1
    ) {
      const optionsResponseType = dataTypeId
        ? {
            response_type: RESPONSE_TYPES.response_from_data_type,
            repeat_on_reprompt: true
          }
        : {
            response_type: 'option',
            repeat_on_reprompt: true,
            options: rpOptions
          };

      const outputHasCustomResponseType = outputGeneric.some(
        response => !response.response_type || response.response_type !== 'text'
      );
      const doesOptionsResponseMatchEntity =
        optionsResponseIndexMatchingEntity >= 0;
      const canAddNewResponseToOutput =
        doesOptionsResponseMatchEntity ||
        outputGeneric.length === 0 ||
        !outputHasCustomResponseType;

      // if we found a matching response type earlier
      if (doesOptionsResponseMatchEntity) {
        outputGeneric[optionsResponseIndexMatchingEntity] =
          optionsResponseType as any;
      } else if (canAddNewResponseToOutput) {
        outputGeneric.push(optionsResponseType as any);
      }
    }
  }

  // update the output generic in the step
  _.set(currentStep, 'output.generic', outputGeneric);

  // update the specific step
  // in the array of steps
  const steps = {
    ...state.steps,
    [stepId]: currentStep
  };

  // if optionsDiffMap was included in the action
  if (optionsDiffMap) {
    const optionsToModify = {};
    // for each option in the diffmap (key is the id of the value)
    Object.keys(optionsDiffMap).forEach(key => {
      // if the value isn't already in the option
      if (!_.find(options, { value: key })) {
        // add the value from the diff map to the optionsToModify
        optionsToModify[key] = optionsDiffMap[key];
      }
    });

    // if we have optionsToModify
    if (Object.keys(optionsToModify).length) {
      // update a bunch of stuff
      const updatedSteps = updateEntityValueReferencesInStepConditions(
        steps,
        entityId,
        optionsToModify
      );
      Object.assign(steps, updatedSteps);
    }
  }
  // assign the updated steps
  state.steps = steps;

  return state;
};

const updateStepContext = (
  oldState: ModelsSlice,
  stepId: string,
  context: StepContext
): ModelsSlice => {
  const state = { ...oldState };
  const step = { ..._.get(state, `steps.${stepId}`) }; //shallow copy of step
  step.invalid_references = { ...step.invalid_references };
  const priorErrorCount = countErrorsInContext(step.context);
  if (context) {
    step.context = context;
    const currentErrorCount = countErrorsInContext(context);
    step.invalid_references.variable =
      step.invalid_references.variable + (currentErrorCount - priorErrorCount);
  } else {
    delete step.context;
    step.invalid_references.variable -= priorErrorCount;
  }
  state.steps = { ...state.steps };
  _.set(state, `steps.${stepId}`, step);
  return state;
};

const updateStepOutput = (
  oldState: ModelsSlice,
  stepId: string,
  jsonData: Output
): ModelsSlice => {
  const state = { ...oldState };
  const step = { ..._.get(state, `steps.${stepId}`) };
  step.invalid_references = { ...step.invalid_references };
  const oldOutput = step.output;
  step.output = jsonData;
  step.responseText = getResponseText(step);
  if (Array.isArray(step.output?.generic)) {
    let existingInvalidVarsCount = 0;
    let latestInvalidVarsCount = 0;
    oldOutput?.generic?.forEach(response => {
      switch (response.response_type) {
        case 'text':
          // whenever it is a text response,
          // we need to check if there are invalid variables
          // used in the text response.
          response.values.forEach(value => {
            existingInvalidVarsCount += value?.text_expression
              ? countErrorsInText(value.text_expression.concat)
              : 0;
          });
          break;
        // In the future, whenever we added a rte-editable response
        // we need to validate variables in the response
        default:
          break;
      }
    });
    step.output.generic.forEach(response => {
      switch (response.response_type) {
        case 'text':
          // whenever it is a text response,
          // we need to check if there are invalid variables
          // used in the text response.
          response.values.forEach(value => {
            latestInvalidVarsCount += value?.text_expression
              ? countErrorsInText(value.text_expression.concat)
              : 0;
          });
          break;
        // In the future, whenever we added a rte-editable response
        // we need to validate variables in the response
        default:
          break;
      }
    });
    // Since RTE can update the output generic array directly,
    // we need to check if there are invalid variables fixed or introduced.
    step.invalid_references.variable =
      step.invalid_references.variable +
      (latestInvalidVarsCount - existingInvalidVarsCount);
  }
  // Since RTE can update the output generic array directly,
  // The variable title is not only depends on a single text response anymore.
  if (!step.title) {
    //if the title is not set we need to update the variable's description
    step.variableTitle = removeVariables(step.responseText);
    let stepVariable = _.get(state, `action_variables.${step.variable}`);
    if (stepVariable) {
      stepVariable = { ...stepVariable };
      stepVariable.title = step.variableTitle;
      state.action_variables = { ...state.action_variables };
      _.set(state, `action_variables.${step.variable}`, stepVariable);
    }
  }
  state.steps = { ...state.steps };
  _.set(state, `steps.${stepId}`, step);
  return state;
};

// Move this to work within defineCustomerResponse
const setStepFreeTextResponse = (
  oldState: ModelsSlice,
  stepId: string,
  deleteResponse: boolean,
  changeResponse: boolean,
  language: string,
  infoGatheringEnabled: boolean
) => {
  let state;
  if (infoGatheringEnabled) {
    state = updateStepValidation(
      oldState,
      stepId,
      3,
      getDefaultErrorResponseByEntityType(ENTITY_TYPES.free_text.id, language, {
        hasOptionResponse: false
      })
    );
  } else {
    state = { ...oldState, steps: { ...oldState.steps } };
  }

  const step = state.steps[stepId];
  const { variable } = step;
  if (changeResponse) {
    const dataTypeId = _.get(step, 'question.data_type');
    const entityId = _.get(step, 'question.entity');
    const entity = _.get(state, `models.entities[${entityId}]`);
    state = removeCustomerResponse(state, stepId, dataTypeId, entityId, entity);
  }

  state.steps[stepId] = _.cloneDeep(state.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  if (state.steps[stepId]?.question) {
    delete state.steps[stepId].question;
  }

  if (!deleteResponse) {
    _.set(state.steps[stepId], 'question.free_text', true);
  }

  if (variable) {
    if (state.action_variables[variable]) {
      state.action_variables[variable].data_type = DATA_TYPE.ANY;
    }
    // invalidate associated conditions (only boolean conditions, not expression conditions)
    if (deleteResponse) {
      return deleteReferencesToStepVariable(state, null, null, variable);
    }
  }
  return state;
};

const setPopulateWhenPrompted = (
  oldState: ModelsSlice,
  stepId: string,
  responseCollectionBehavior: boolean
): ModelsSlice => {
  const state = { ...oldState, steps: { ...oldState.steps } };

  state.steps[stepId] = _.cloneDeep(state.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  _.set(
    state.steps[stepId],
    'question.response_collection_behavior',
    responseCollectionBehavior
  );

  return state;
};

const setAllowTopicSwitch = (
  oldState: ModelsSlice,
  { stepId, enabled }: { stepId: string; enabled: boolean }
): ModelsSlice => {
  const state = { ...oldState, steps: { ...oldState.steps } };

  state.steps[stepId] = _.cloneDeep(state.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  _.set(state.steps[stepId], 'question.allow_topic_switch', enabled);
  return state;
};

//sets privacy to action variables
const setPrivacy = (
  oldState: ModelsSlice,
  { stepId, enabled }: { stepId: string; enabled: boolean }
): ModelsSlice => {
  const state = {
    ...oldState,
    action_variables: { ...oldState.action_variables }
  };
  state.action_variables[stepId] = _.cloneDeep(state.action_variables[stepId]);
  _.set(state.action_variables[stepId], 'privacy.enabled', enabled);
  return state;
};

//sets privacy to action variables
const setCollectVerbatim = (
  oldState: ModelsSlice,
  { stepId, enabled }: { stepId: string; enabled: boolean }
): ModelsSlice => {
  const state = {
    ...oldState,
    steps: { ...oldState.steps }
  };

  state.steps[stepId] = _.cloneDeep(state.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  _.set(state.steps[stepId], 'question.collect_verbatim_response', enabled);
  return state;
};

//set the LLM Slot filling examples
const setSlotFillingExamples = (
  oldState: ModelsSlice,
  { stepId, examples }: { stepId: string; examples }
): ModelsSlice => {
  const state = {
    ...oldState,
    steps: { ...oldState.steps }
  };

  state.steps[stepId] = _.cloneDeep(state.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  _.set(state.steps[stepId], 'examples', examples);
  return state;
};

const updateDynamicOptions = (
  state: ModelsSlice,
  { stepId, question }: { stepId: string; question: StepQuestion }
) => {
  const newState = { ...state, steps: { ...state.steps } };
  newState.steps[stepId] = _.cloneDeep(newState.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  _.set(newState.steps[stepId], 'question', {
    ...question
  });
  return newState;
};
const addResponseFromVariable = (
  state: ModelsSlice,
  {
    stepId,
    language,
    launchModeSettings
  }: {
    stepId: string;
    language: string;
    launchModeSettings: { enabled?: boolean };
  }
) => {
  const defaultNumOfTurns = launchModeSettings?.enabled ? undefined : 3;

  const newState = updateStepValidation(
    state,
    stepId,
    defaultNumOfTurns,
    getDefaultErrorResponseByEntityType(
      ENTITY_TYPES.dynamic_option.id,
      language
    )
  );

  newState.steps[stepId] = _.cloneDeep(newState.steps[stepId]); //need to clone the step as we are modifying it 'deeply'
  const genericOutput = newState.steps[stepId]?.output?.generic ?? [];
  genericOutput.push({
    response_type: 'response_from_variable',
    repeat_on_reprompt: true
  });
  _.set(newState.steps[stepId], 'output.generic', genericOutput);
  return newState;
};

export const reducer = (state: ModelsSlice, action: AnyAction): ModelsSlice => {
  switch (action.type) {
    case actionTypes.ACTIONS_SET_TITLE:
      return setActionTitle(state, action.actionId, action.title);
    case actionTypes.ACTIONS_SAVE:
      return updateActionOnSave(
        state,
        action.actionId,
        action.condition,
        action.updated,
        action.entitiesToClear
      );
    case actionTypes.ACTIONS_GET_START:
      return { ...state, steps: {} };
    case actionTypes.ACTIONS_ADD_DISPLAY_NAME:
      return setDisplayName(state, action.actionId, action.displayName);
    case actionTypes.ACTIONS_ADD_EXAMPLE:
    case actionTypes.ACTIONS_DELETE_EXAMPLE:
    case actionTypes.ACTIONS_UPDATE_EXAMPLE:
      return updateActionTempTitle(state, action.actionId);
    case actionTypes.ACTION_UPDATE_CONDITION:
      return updateActionCondition(state, action.actionId, action.condition);
    case actionTypes.STEP_CREATE:
      return addStepToAction(
        state,
        action.actionId,
        action.stepId,
        action.newStepIndex,
        action.newStep
      );
    case actionTypes.STEP_DELETE:
      return deleteStep(
        state,
        action.actionId,
        action.entityId,
        action.stepId,
        action.variableId,
        action.resultVariableId
      );
    case actionTypes.STEP_REORDER:
      return reorderSteps(
        state,
        action.actionId,
        action.sourceIndex,
        action.destinationIndex
      );
    case actionTypes.STEP_SET_TITLE:
      return setStepTitle(state, action.stepId, action.title);
    case actionTypes.STEP_SET_TEXT:
      return setStepText(
        state,
        action.stepId,
        action.text,
        action.defaultValue
      );
    case actionTypes.SET_STEP_MAX_HITS:
      return updateStepMaxHits(state, action.stepId, action.maxHits);
    case actionTypes.STEP_UPDATE_CONDITION:
      return updateStepCondition(state, action.stepId, action.condition);
    case actionTypes.STEP_ADD_DATEPICKER:
      return updateDatepicker(state, action.stepId, action.entityId);
    case actionTypes.STEP_ADD_YES_NO_OPTIONS:
      return updateStepYesNoOptions(
        state,
        action.language,
        action.stepId,
        action.entityId
      );
    case actionTypes.STEP_UPDATE_OPTIONS:
      return updateStepOptions(
        state,
        action.actionId,
        action.stepId,
        action.dataTypeOperation,
        action.dataTypeId,
        action.entityId,
        action.options,
        action.optionsDiffMap,
        action.entity
      );
    case actionTypes.STEP_REMOVE_OPTIONS:
      return deleteStepOptions(
        state,
        action.stepId,
        action.dataTypeId,
        action.entity,
        action.entityId
      );
    case actionTypes.STEP_DEFINE_CUSTOMER_RESPONSE:
      return defineCustomerResponse(
        state,
        action.stepId,
        action.dataTypeId,
        action.entityId,
        action.language,
        action.launchModeSettings
      );
    case actionTypes.STEP_REMOVE_CUSTOMER_RESPONSE:
      return removeCustomerResponse(
        state,
        action.stepId,
        action.dataTypeId,
        action.entityId,
        action.entity
      );
    case actionTypes.STEP_UPDATE_VALIDATION:
      return updateStepValidation(
        state,
        action.stepId,
        action.numberOfTurns,
        action.respondWith,
        action.useLaunchModeDefault,
        action.inputValidationHandler,
        action.repeatOnReprompt
      );
    case actionTypes.STEP_UPDATE_END_BEHAVIOR:
      return updateEndStepBehavior(
        state,
        action.actionId,
        action.stepId,
        action.resolverType,
        action.updatedResolver
      );
    case actionTypes.STEP_UPDATE_CONTEXT:
      return updateStepContext(state, action.stepId, action.context);
    case actionTypes.STEP_UPDATE_OUTPUT:
      return updateStepOutput(state, action.stepId, action.jsonData);
    case actionTypes.STEP_SET_FREE_TEXT_RESPONSE:
      return setStepFreeTextResponse(
        state,
        action.stepId,
        action.deleteResponse,
        action.changeResponse,
        action.language,
        action.infoGatheringEnabled
      );
    case actionTypes.STEP_UPDATE_RESPONSE_COLLECTION_BEHAVIOR:
      return setPopulateWhenPrompted(
        state,
        action.stepId,
        action.responseCollectionBehavior
      );
    case updateAllowTopicSwitch.type:
      return setAllowTopicSwitch(state, action.payload);
    case setPrivacyOnStep.type:
      return setPrivacy(state, action.payload);
    case setCollectVerbatimOnStep.type:
      return setCollectVerbatim(state, action.payload);
    case setSlotExamplesOnStep.type:
      return setSlotFillingExamples(state, action.payload);
    case actionTypes.STEP_UPDATE_REFERENCED_ENTITY:
      return updateEntityReferencesToStepVariable(
        state,
        action.payload.stepId,
        action.payload.oldEntityId,
        action.payload.newEntityId,
        action.payload.variableId
      );
    case updateStepDynamicOptions.type:
      return updateDynamicOptions(state, action.payload);
    case addResponseFromVariableResponse.type:
      return addResponseFromVariable(state, action.payload);
    default:
      return state;
  }
};
